/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2026. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */

import { defineStore } from 'pinia';
import BackupModel from '@/model/backup/backup.model';
import { BACKUP_STATUSES } from '@/model/node/sub-models/deployed-workload.model';
import { BackupsApiService } from '@/services/api';
import useUtilsStore from '@/store/modules/pinia/utils/_api_request_handler';

const useVmBackupsStore = defineStore('vmBackups', {
  state: () => ({
    vmBackupList: [],
    deviceId: '',
    nodeSerialNumber: '',
  }),
  actions: {
    async fetch(deviceId, serialNumber) {
      try {
        // If either deviceId or serialNumber does not exist axios request should not be sent
        if (!deviceId || !serialNumber) {
          return;
        }
        let backups = await BackupsApiService.fetchBackups({ deviceId, serialNumber });
        backups.sort((a, b) => (a.started > b.started ? -1 : b.started > a.started ? 1 : 0));
        backups = backups.map((el) => {
          el.started = new Date(el.started).toLocaleString('en-GB');
          el.finished = new Date(el.finished).toLocaleString('en-GB');
          if (el.error) {
            el.timeOfError = el.finished;
          }
          if (el.status !== BACKUP_STATUSES.COMPLETED) {
            el.finished = '';
          }
          return el;
        });
        this.vmBackupList = backups.map((backup) => new BackupModel(backup));
      } catch (e) {
        this.vmBackupList = [];
        throw e;
      }
    },
    async createBackup(params) {
      const utilsStore = useUtilsStore();
      try {
        utilsStore.showLoadingBar();
        await BackupsApiService.createBackup(params);
        utilsStore.showCustomToast({
          text: 'workloadManagement.creatingABackupInProgress',
          color: 'success',
          showClose: true,
        });
      } catch (e) {
        console.debug(e);
      } finally {
        utilsStore.closeLoadingBar();
      }
    },
    async retryDeploy(params) {
      const utilsStore = useUtilsStore();
      try {
        utilsStore.showLoadingBar();
        if (params?.reqObj?.workloadId && params?.reqObj?.versionId) {
          params.reqObj.workloadId = params.reqObj.workloadId?.split('-')[0];
          params.reqObj.versionId = params.reqObj.versionId?.split('-')[0];
        }
        await BackupsApiService.retryBackup({
          serialNumber: this.nodeSerialNumber,
          deviceId: params.deviceId,
          reqObj: params.reqObj,
        });
        utilsStore.showCustomToast({
          text: 'workloadManagement.creatingABackupInProgress',
          color: 'success',
          showClose: true,
        });
      } catch (err) {
        console.debug(err);
      } finally {
        utilsStore.closeLoadingBar();
      }
    },
    setDeviceId(id) {
      this.deviceId = id;
    },
    setSerialNumber(serialNumber) {
      this.nodeSerialNumber = serialNumber;
    },
    mqttBackups(data) {
      const { deviceId, backup } = data.params;
      if (this.deviceId !== deviceId) {
        return;
      }
      backup.started = new Date(backup.started).toLocaleString('en-GB');
      backup.finished = backup.finished ? new Date(backup.finished).toLocaleString('en-GB') : '';

      const found = this.vmBackupList.find((el) => el.name === backup.name);
      if (!found) {
        this.vmBackupList.unshift(new BackupModel(backup));
        return;
      }
      if (backup.error) {
        found.error = backup.error;
        found.timeOfError = backup.finished;
        return;
      }
      found.started = backup.started;
      found.status = backup.status;
      found.finished = backup.status !== BACKUP_STATUSES.COMPLETED ? '' : backup.finished;
      found.progress = backup.progress || 0;
    },
  },
});

export default useVmBackupsStore;
